import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    DollarSign, Loader2, AlertTriangle, Save, ExternalLink, ChevronDown, ChevronUp, History, Plus, Trash2,
} from 'lucide-react';

const ACCENT = '#7C6BEF';

const RISK_META = {
    low: { label: 'Bajo', color: '#00875A' },
    medium: { label: 'Medio', color: '#F59E0B' },
    high: { label: 'Alto', color: '#EF4444' },
};

const METRIC_META = {
    rooms: { label: 'Habitaciones', unit: 'habitaciones' },
    tables: { label: 'Mesas', unit: 'mesas' },
    collaborators: { label: 'Colaboradores', unit: 'colaboradores' },
    sqm: { label: 'm² de construcción', unit: 'm²' },
};

function rubroIsDefined(rubro) {
    const ranges = rubro.price_ranges || [];
    return ranges.length > 0 && ranges.every((r) => r.price_mxn != null);
}

/**
 * Iter 73 · reemplaza el precio único por rango de precio (según el dato de
 * tamaño del cliente: habitaciones/mesas/colaboradores/m²). El admin define
 * tanto los cortes como el precio de cada rango — ninguno se precarga.
 * Un rubro cuenta como "definido" solo cuando TODOS sus rangos tienen precio.
 */
export default function AdminSettingsPricing() {
    const [rubros, setRubros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [drafts, setDrafts] = useState({}); // rubro_id -> [{id, min_value, max_value, price_mxn}] (strings)
    const [savingId, setSavingId] = useState(null);
    const [log, setLog] = useState([]);
    const [showLog, setShowLog] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/rubros');
            const sorted = (r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setRubros(sorted);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar rubros'));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const loadLog = async () => {
        try {
            const r = await api.get('/admin/pricing-change-log');
            setLog(r.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar historial'));
        }
    };

    const missingCount = useMemo(() => rubros.filter((r) => !rubroIsDefined(r)).length, [rubros]);

    const toggleExpand = (rubro) => {
        if (expandedId === rubro.id) { setExpandedId(null); return; }
        setExpandedId(rubro.id);
        setDrafts((prev) => ({
            ...prev,
            [rubro.id]: (rubro.price_ranges || []).map((r) => ({
                id: r.id,
                min_value: String(r.min_value ?? 0),
                max_value: r.max_value == null ? '' : String(r.max_value),
                price_mxn: r.price_mxn == null ? '' : String(r.price_mxn),
            })),
        }));
    };

    const addRange = (rubroId) => {
        setDrafts((prev) => ({
            ...prev,
            [rubroId]: [...(prev[rubroId] || []), { id: `new-${Date.now()}`, min_value: '', max_value: '', price_mxn: '' }],
        }));
    };

    const removeRange = (rubroId, idx) => {
        setDrafts((prev) => ({ ...prev, [rubroId]: prev[rubroId].filter((_, i) => i !== idx) }));
    };

    const updateRange = (rubroId, idx, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [rubroId]: prev[rubroId].map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
        }));
    };

    const saveRanges = async (rubroId) => {
        const draft = drafts[rubroId] || [];
        for (const r of draft) {
            if (r.min_value.trim() === '') { toast.error('Todos los rangos necesitan un mínimo'); return; }
        }
        setSavingId(rubroId);
        try {
            const payload = {
                price_ranges: draft.map((r) => ({
                    min_value: Number(r.min_value),
                    max_value: r.max_value.trim() === '' ? null : Number(r.max_value),
                    price_mxn: r.price_mxn.trim() === '' ? null : Number(r.price_mxn),
                })),
            };
            await api.patch(`/admin/rubros/${rubroId}/price-ranges`, payload);
            toast.success('Rangos guardados');
            await load();
            if (showLog) loadLog();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar rangos'));
        } finally {
            setSavingId(null);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <PageHeader title="Costos por Rubro" subtitle="◆ Configuración de Precios por Rango" accent={ACCENT} />

            {missingCount > 0 ? (
                <div className="mb-5 p-4 border-l-4 flex items-start gap-3" data-testid="pricing-missing-banner"
                    style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)' }}>
                    <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#F59E0B' }} />
                    <div>
                        <div className="font-bold text-sm">{missingCount} de {rubros.length} rubros sin precio definido</div>
                        <p className="text-xs opacity-70 mt-0.5">
                            Un rubro se considera definido solo cuando TODOS sus rangos de precio tienen un valor.
                            Mientras falte, ese rubro específico muestra la cotización pendiente a sus clientes en /client/requests/new
                            (los demás rubros no se ven afectados).
                        </p>
                    </div>
                </div>
            ) : (
                <div className="mb-5 p-4 border-l-4 flex items-start gap-3" data-testid="pricing-complete-banner"
                    style={{ borderColor: '#00875A', background: 'rgba(0,135,90,0.08)' }}>
                    <DollarSign className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#00875A' }} />
                    <div className="font-bold text-sm">Todos los rubros tienen sus rangos de precio definidos.</div>
                </div>
            )}

            <div className="mb-4 text-xs opacity-70 flex items-center gap-1.5">
                <Link to="/admin/pricing" className="underline font-bold inline-flex items-center gap-1">
                    Ver matriz de precios anterior (legacy) <ExternalLink className="w-3 h-3" />
                </Link>
                <span>— ya no se usa para calcular precios de cliente.</span>
            </div>

            <div className="border-2 overflow-hidden" style={{ borderColor: 'var(--border)' }} data-testid="pricing-rubros-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ background: 'rgba(124,107,239,0.06)' }}>
                            <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider opacity-70">Rubro</th>
                            <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider opacity-70">Métrica de tamaño</th>
                            <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider opacity-70">Estado</th>
                            <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider opacity-70">Riesgo</th>
                            <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider opacity-70" />
                        </tr>
                    </thead>
                    <tbody>
                        {rubros.map((r) => {
                            const metric = METRIC_META[r.pricing_metric] || METRIC_META.sqm;
                            const defined = rubroIsDefined(r);
                            const rangeCount = (r.price_ranges || []).length;
                            const expanded = expandedId === r.id;
                            return (
                                <React.Fragment key={r.id}>
                                    <tr className="border-t" style={{ borderColor: 'var(--border)' }} data-testid={`pricing-row-${r.id}`}>
                                        <td className="px-4 py-2">
                                            <div className="font-bold">{r.name}</div>
                                            <div className="text-xs opacity-60">{r.category}</div>
                                        </td>
                                        <td className="px-4 py-2 text-xs opacity-80">{metric.label}</td>
                                        <td className="px-4 py-2">
                                            {defined ? (
                                                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                                                    style={{ background: 'rgba(0,135,90,0.15)', color: '#00875A' }}>
                                                    {rangeCount} rango{rangeCount === 1 ? '' : 's'} definido{rangeCount === 1 ? '' : 's'}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                                                    style={{ background: 'rgba(245,158,11,0.15)', color: '#B45309' }}>
                                                    {rangeCount === 0 ? 'Sin definir' : `Incompleto (${rangeCount})`}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className="font-bold px-2 py-0.5 text-xs"
                                                style={{ background: `${RISK_META[r.risk_level]?.color || '#7C6BEF'}15`, color: RISK_META[r.risk_level]?.color || '#7C6BEF' }}>
                                                {RISK_META[r.risk_level]?.label || 'Medio'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <button type="button" onClick={() => toggleExpand(r)} data-testid={`pricing-toggle-${r.id}`}
                                                className="text-xs font-bold underline" style={{ color: ACCENT }}>
                                                {expanded ? 'Cerrar' : 'Editar rangos'}
                                            </button>
                                        </td>
                                    </tr>
                                    {expanded && (
                                        <tr style={{ background: 'rgba(124,107,239,0.03)' }}>
                                            <td colSpan={5} className="px-4 py-4">
                                                <RangeEditor
                                                    rubroId={r.id}
                                                    unit={metric.unit}
                                                    ranges={drafts[r.id] || []}
                                                    onAdd={() => addRange(r.id)}
                                                    onRemove={(idx) => removeRange(r.id, idx)}
                                                    onChange={(idx, field, val) => updateRange(r.id, idx, field, val)}
                                                    onSave={() => saveRanges(r.id)}
                                                    saving={savingId === r.id}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Historial de cambios — trazabilidad (colapsado por defecto) */}
            <div className="mt-8">
                <button onClick={() => { setShowLog((v) => !v); if (!showLog) loadLog(); }}
                    data-testid="pricing-log-toggle"
                    className="text-xs font-bold uppercase tracking-wider opacity-70 inline-flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" />
                    Historial de cambios
                    {showLog ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showLog && (
                    <div className="mt-3 border-2 divide-y" style={{ borderColor: 'var(--border)' }} data-testid="pricing-log-list">
                        {log.length === 0 ? (
                            <div className="p-4 text-sm opacity-60">Sin cambios registrados todavía.</div>
                        ) : log.map((entry) => (
                            <div key={entry.id} className="p-3 text-xs flex items-center justify-between gap-3" style={{ borderColor: 'var(--border)' }}>
                                <div>
                                    <span className="font-bold">{entry.rubro_name}</span>
                                    <span className="opacity-60"> · {(entry.before_price_ranges || []).length} rango(s) → </span>
                                    <span className="font-bold">{(entry.after_price_ranges || []).length} rango(s)</span>
                                </div>
                                <div className="opacity-60 text-right shrink-0">
                                    {entry.actor_email || entry.actor_id}<br />
                                    {new Date(entry.applied_at).toLocaleString('es-MX')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function RangeEditor({ rubroId, unit, ranges, onAdd, onRemove, onChange, onSave, saving }) {
    return (
        <div data-testid={`range-editor-${rubroId}`}>
            {ranges.length === 0 ? (
                <div className="text-xs opacity-60 mb-3">Sin rangos definidos todavía — agrega el primero.</div>
            ) : (
                <div className="space-y-2 mb-3">
                    <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[10px] uppercase tracking-wider font-bold opacity-60">
                        <span>Mínimo ({unit})</span>
                        <span>Máximo ({unit}, vacío = sin límite)</span>
                        <span>Precio (MXN)</span>
                        <span />
                    </div>
                    {ranges.map((r, idx) => (
                        <div key={r.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`range-row-${rubroId}-${idx}`}>
                            <input type="number" min="0" value={r.min_value}
                                onChange={(e) => onChange(idx, 'min_value', e.target.value)}
                                data-testid={`range-min-${rubroId}-${idx}`}
                                placeholder="0" className="px-2 py-1.5 border-2 outline-none text-sm"
                                style={{ borderColor: 'var(--border)', background: 'transparent' }} />
                            <input type="number" min="0" value={r.max_value}
                                onChange={(e) => onChange(idx, 'max_value', e.target.value)}
                                data-testid={`range-max-${rubroId}-${idx}`}
                                placeholder="Sin límite" className="px-2 py-1.5 border-2 outline-none text-sm"
                                style={{ borderColor: 'var(--border)', background: 'transparent' }} />
                            <input type="number" min="0" value={r.price_mxn}
                                onChange={(e) => onChange(idx, 'price_mxn', e.target.value)}
                                data-testid={`range-price-${rubroId}-${idx}`}
                                placeholder="Sin definir" className="px-2 py-1.5 border-2 outline-none text-sm"
                                style={{ borderColor: r.price_mxn.trim() === '' ? '#F59E0B' : 'var(--border)', background: 'transparent' }} />
                            <button type="button" onClick={() => onRemove(idx)} data-testid={`range-remove-${rubroId}-${idx}`}
                                className="px-2 text-red-600 hover:opacity-70">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-center gap-2">
                <button type="button" onClick={onAdd} data-testid={`range-add-${rubroId}`}
                    className="border-2 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5"
                    style={{ borderColor: ACCENT, color: ACCENT }}>
                    <Plus className="w-3.5 h-3.5" /> Agregar rango
                </button>
                <button type="button" onClick={onSave} disabled={saving} data-testid={`range-save-${rubroId}`}
                    className="text-white px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: ACCENT }}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Guardar rangos
                </button>
            </div>
        </div>
    );
}
